import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useDepositSettings } from "@/hooks/useDepositSettings";
import { ContactInfo } from "@/components/ContactInfo";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { X, DollarSign, Clock, AlertTriangle, Sparkles } from "lucide-react";

const CancellationPolicy = () => {
  const { depositAmount } = useDepositSettings();
  const depositLabel = depositAmount > 0 ? `$${depositAmount}` : "";
  return (
    <div className="min-h-screen bg-background">
      <SEO
        path="/cancellation-policy"
        title="Cancellation Policy — In Paradise Beauty"
        description="Learn about In Paradise Beauty's cancellation, rescheduling, deposit, and late-arrival policies."
      />
      <Navbar />
      <section className="pt-32 pb-24">
        <div className="container mx-auto px-6 max-w-3xl">
          <div className="text-center mb-12">
            <p className="text-primary text-xs font-light tracking-[0.5em] uppercase mb-3">Legal</p>
            <h1 className="font-serif text-4xl md:text-5xl font-bold italic">Cancellation Policy</h1>
          </div>

          <div className="prose prose-sm max-w-none text-muted-foreground font-light leading-[2] tracking-wide space-y-8">
            <p className="text-xs text-muted-foreground/70 uppercase tracking-[0.3em]">
              Last Updated: February 21, 2026
            </p>

            <p>
              We value your time and ours. Please review the following policies carefully before booking your appointment with In Paradise Beauty.
            </p>

            {/* Policy cards */}
            <div className="grid grid-cols-1 gap-6 not-prose">
              <div className="border border-border/50 p-8">
                <div className="flex items-center gap-3 mb-4">
                  <X size={24} className="text-primary" />
                  <h3 className="font-serif text-lg font-semibold text-foreground italic">Cancellation & Rescheduling</h3>
                </div>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground text-sm font-light leading-[2]">
                  <li>Please cancel or reschedule at least <strong className="text-foreground">24 hours</strong> before your appointment.</li>
                  <li>A new deposit is required if you reschedule within 24 hours of your appointment time.</li>
                  <li>No-call, no-show clients will be charged <strong className="text-foreground">half of the service price</strong>.</li>
                  <li>Repeated no-shows may result in being unable to book future appointments.</li>
                </ul>
              </div>

              <div className="border border-border/50 p-8">
                <div className="flex items-center gap-3 mb-4">
                  <DollarSign size={24} className="text-primary" />
                  <h3 className="font-serif text-lg font-semibold text-foreground italic">Deposits</h3>
                </div>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground text-sm font-light leading-[2]">
                  <li>All services require a <strong className="text-foreground">non-refundable {depositLabel ? `${depositLabel} ` : ""}deposit</strong> to secure your appointment.</li>
                  <li>The deposit will be applied toward the total cost of your service.</li>
                  <li>If you cancel with at least 24 hours' notice, your deposit can be applied to a future appointment.</li>
                </ul>
              </div>

              <div className="border border-border/50 p-8">
                <div className="flex items-center gap-3 mb-4">
                  <Clock size={24} className="text-primary" />
                  <h3 className="font-serif text-lg font-semibold text-foreground italic">Late Arrivals</h3>
                </div>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground text-sm font-light leading-[2]">
                  <li>Please arrive on time for your scheduled appointment.</li>
                  <li>A <strong className="text-foreground">10-minute grace period</strong> is provided; a <strong className="text-foreground">$10 late fee</strong> will apply after that.</li>
                  <li>Appointments arriving <strong className="text-foreground">20+ minutes late</strong> will be automatically cancelled.</li>
                </ul>
              </div>

              <div className="border border-border/50 p-8">
                <div className="flex items-center gap-3 mb-4">
                  <Sparkles size={24} className="text-primary" />
                  <h3 className="font-serif text-lg font-semibold text-foreground italic">Bare Nails / Feet / Lashes</h3>
                </div>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground text-sm font-light leading-[2]">
                  <li>Please arrive with <strong className="text-foreground">bare nails or lashes</strong> for your appointment.</li>
                  <li>An additional <strong className="text-foreground">15% fee</strong> will be added to your total for any existing work that has to be removed.</li>
                </ul>
              </div>

              <div className="border border-border/50 p-8">
                <div className="flex items-center gap-3 mb-4">
                  <AlertTriangle size={24} className="text-primary" />
                  <h3 className="font-serif text-lg font-semibold text-foreground italic">Refund Policy</h3>
                </div>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground text-sm font-light leading-[2]">
                  <li>Deposits are non-refundable except in cases of emergency at the salon's discretion.</li>
                  <li>If you are dissatisfied with a service, please contact us within 48 hours so we can address your concerns.</li>
                  <li>Refunds for completed services are evaluated on a case-by-case basis.</li>
                </ul>
              </div>
            </div>

            <div>
              <h2 className="font-serif text-xl font-semibold text-foreground italic mb-3">Contact Us</h2>
              <p>
                If you need to cancel or reschedule, or have questions about the policies, please contact us:
              </p>
              <ContactInfo />
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default CancellationPolicy;
